import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { AppState } from '../app-store.state';
import { generateUniqueId } from '../app-store.functions';
import { TemplateDraft as Entity, TemplateDraftStatus, FinalizeTemplateDraftResult, FinalizeTemplateDraftRequest } from './template-draft.model';
import { Template } from '../template/template.model';
import { TemplateDraftActions as Actions } from './template-draft.actions';
import { TemplateDraftSelectors as Selectors } from './template-draft.selectors';

@Injectable({ providedIn: 'root' })
export class TemplateDraftFacade {
    all$: Observable<Entity[]> = this.store.pipe(select(Selectors.selectAll));
    isLoading$: Observable<boolean> = this.store.pipe(select(Selectors.isLoading));
    hasError$: Observable<Error | undefined> = this.store.pipe(select(Selectors.hasErrors));
    current$: Observable<Entity | undefined> = this.store.pipe(select(Selectors.currentEntity));
    currentId$: Observable<number | undefined> = this.store.pipe(select(Selectors.currentEntityId));
    currentCategoryId$: Observable<number | undefined> = this.store.pipe(select(Selectors.currentCategoryId));

    constructor(private store: Store<AppState>) {}

    getById(draftId: number): Observable<Entity | undefined> {
        return this.store.pipe(select(Selectors.selectById(draftId)));
    }

    getByCategory(categoryId: number): Observable<Entity[]> {
        return this.store.pipe(select(Selectors.selectByCategory(categoryId)));
    }

    setCurrentDraftId(draftId: number | undefined): void {
        this.store.dispatch(Actions.setCurrentDraftId({ draftId }));
    }

    setCurrentCategoryId(categoryId: number | undefined): void {
        this.store.dispatch(Actions.setCurrentCategoryId({ categoryId }));
    }

    clearStore(): void {
        this.store.dispatch(Actions.clearStore());
    }

    // Load operations
    loadSingle(draftId: number): void {
        this.store.dispatch(Actions.loadSingle({ draftId }));
    }

    loadSingleWithResult(draftId: number): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.loadSingle({ draftId, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    loadByCategory(categoryId: number, status?: TemplateDraftStatus): void {
        this.store.dispatch(Actions.loadByCategory({ categoryId, status }));
    }

    loadByCategoryWithResult(categoryId: number, status?: TemplateDraftStatus): Observable<Entity[]> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.loadByCategory({ categoryId, status, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity[]>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    // Create operations
    create(entity: Partial<Entity>): void {
        this.store.dispatch(Actions.create({ entity }));
    }

    createWithResult(entity: Partial<Entity>): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.create({ entity, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    // Update operations
    update(entity: Entity): void {
        this.store.dispatch(Actions.update({ entity }));
    }

    updateWithResult(entity: Entity): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.update({ entity, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    // Remove operations
    remove(draftId: number): void {
        this.store.dispatch(Actions.remove({ draftId }));
    }

    removeWithResult(draftId: number): Observable<number> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.remove({ draftId, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<number>(correlationId))
            .pipe(filter((x) => x !== undefined), first());
    }

    // Add templates to draft
    addTemplates(draftId: number, productIds: number[]): void {
        this.store.dispatch(Actions.addTemplates({ draftId, productIds }));
    }

    addTemplatesWithResult(draftId: number, productIds: number[]): Observable<Template[]> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.addTemplates({ draftId, productIds, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Template[]>(correlationId))
            .pipe(filter((x) => x !== undefined), first());
    }

    // Finalize draft
    finalize(draftId: number, request?: FinalizeTemplateDraftRequest): void {
        this.store.dispatch(Actions.finalize({ draftId, request }));
    }

    finalizeWithResult(draftId: number, request?: FinalizeTemplateDraftRequest): Observable<FinalizeTemplateDraftResult> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.finalize({ draftId, request, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<FinalizeTemplateDraftResult>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    // Start edit (reopen completed draft)
    startEdit(draftId: number): void {
        this.store.dispatch(Actions.startEdit({ draftId }));
    }

    startEditWithResult(draftId: number): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.startEdit({ draftId, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    // Edit existing template (create draft from it)
    editTemplate(templateId: number): void {
        this.store.dispatch(Actions.editTemplate({ templateId }));
    }

    editTemplateWithResult(templateId: number): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.editTemplate({ templateId, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    // Edit multiple existing templates (create draft from them)
    editTemplates(templateIds: number[]): void {
        this.store.dispatch(Actions.editTemplates({ templateIds }));
    }

    editTemplatesWithResult(templateIds: number[]): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.editTemplates({ templateIds, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    // Create order from existing templates (creates OneTime draft)
    createOrderWithResult(templateIds: number[], bundleIds: number[], categoryIds: number[]): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.createOrder({ templateIds, bundleIds, categoryIds, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

    // Edit selection (create draft from templates/bundles/categories for editing)
    editSelectionWithResult(templateIds: number[], bundleIds: number[], categoryIds: number[]): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.editSelection({ templateIds, bundleIds, categoryIds, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x), first());
    }

}
